/**
 * Apartment editor — US 02.02/02.03/02.10, pure DOM (106: "the editor
 * apartment view is DOM-only, not a scene"). Opened with H.
 *
 * Every mutation is a server op; the server owns the money, the band maths
 * and the anti-soft-lock check. Undo/redo (≥20 steps) covers furniture ops,
 * which are freely reversible — tile and staircase PURCHASES are not
 * undoable, by design (money moved).
 */
import { worldBus } from '@shared/bus.js';
import { callDialogue } from '@transport/api.js';

interface PlacedFurniture {
  id: string;
  type: string;
  x: number;
  y: number;
  rotation: 0 | 90 | 180 | 270;
}
interface FloorState {
  index: number;
  tiles: { x: number; y: number; kind: 'indoor' | 'garden' }[];
  furniture: PlacedFurniture[];
  staircaseUp?: { x: number; y: number };
}
interface View {
  storage?: { itemId: string; qty: number; slot: number }[];
  storageCapacity?: number;
  apartment: {
    floors: FloorState[];
    doorTile: { floor: 0; x: number; y: number };
    stored: { id: string; type: string }[];
  };
  aura: number;
  metaMoney: number;
  totalTiles: number;
  nextTileCost: number;
  staircaseCost: number;
  staircaseUnlockAt: number;
  catalog: { type: string; name: string; footprint: { w: number; h: number }; tier: number }[];
}

type Mode =
  | { kind: 'buy'; tileKind: 'indoor' | 'garden' }
  | { kind: 'place'; furnitureId: string; rotation: 0 | 90 | 180 | 270 }
  | { kind: 'remove' }
  | { kind: 'staircase' };

type UndoEntry = { undo: () => Promise<unknown>; redo: () => Promise<unknown> };

export function initApartmentEditor(root: HTMLElement): void {
  const panel = document.createElement('div');
  panel.className = 'wca-apartment hidden';
  root.appendChild(panel);

  let open = false;
  let view: View | null = null;
  let floor = 0;
  let mode: Mode = { kind: 'buy', tileKind: 'indoor' };
  let notice = '';
  const undoStack: UndoEntry[] = [];
  const redoStack: UndoEntry[] = [];

  async function refresh(): Promise<void> {
    const r = await callDialogue<View>({ op: 'apartment' });
    if (r.ok) {
      view = r.result;
      if (open) render();
    }
  }

  async function act(op: Parameters<typeof callDialogue>[0], record?: UndoEntry): Promise<boolean> {
    const r = await callDialogue<View>(op);
    if (r.ok) {
      view = r.result;
      notice = '';
      if (record) {
        undoStack.push(record);
        if (undoStack.length > 25) undoStack.shift();
        redoStack.length = 0;
      }
    } else {
      notice =
        r.error === 'PATH_BLOCKED'
          ? `Path blocked — ${(r as unknown as { blockedObject?: string }).blockedObject ?? 'object'} would be unreachable`
          : r.message || r.error;
    }
    render();
    return r.ok;
  }

  function cellButton(x: number, y: number, f: FloorState): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = 'wca-apt-cell';
    const tile = f.tiles.find((t) => t.x === x && t.y === y);
    const isDoor =
      floor === 0 && view!.apartment.doorTile.x === x && view!.apartment.doorTile.y === y;
    const stair = f.staircaseUp && f.staircaseUp.x === x && f.staircaseUp.y === y;
    const piece = f.furniture.find((p) => {
      const def = view!.catalog.find((c) => c.type === p.type);
      const w = p.rotation % 180 === 0 ? (def?.footprint.w ?? 1) : (def?.footprint.h ?? 1);
      const h = p.rotation % 180 === 0 ? (def?.footprint.h ?? 1) : (def?.footprint.w ?? 1);
      return x >= p.x && x < p.x + w && y >= p.y && y < p.y + h;
    });
    if (!tile) b.classList.add('wca-apt-unowned');
    else if (tile.kind === 'garden') b.classList.add('wca-apt-garden');
    if (isDoor) b.textContent = '🚪';
    else if (stair) b.textContent = '🪜';
    else if (piece) {
      b.textContent = (view!.catalog.find((c) => c.type === piece.type)?.name ?? piece.type).slice(
        0,
        2,
      );
      b.title = piece.type;
    }
    b.addEventListener('click', () => void onCell(x, y, Boolean(tile), piece));
    return b;
  }

  async function onCell(
    x: number,
    y: number,
    owned: boolean,
    piece: PlacedFurniture | undefined,
  ): Promise<void> {
    if (mode.kind === 'buy' && !owned) {
      await act({ op: 'buy_tile', floor, x, y, kind: mode.tileKind });
    } else if (mode.kind === 'place' && owned) {
      const m = mode;
      const ok = await act(
        { op: 'place_furniture', floor, furnitureId: m.furnitureId, x, y, rotation: m.rotation },
        {
          undo: () => callDialogue({ op: 'remove_furniture', furnitureId: m.furnitureId }),
          redo: () =>
            callDialogue({
              op: 'place_furniture',
              floor,
              furnitureId: m.furnitureId,
              x,
              y,
              rotation: m.rotation,
            }),
        },
      );
      if (ok) mode = { kind: 'remove' };
    } else if (mode.kind === 'remove' && piece) {
      const p = piece;
      const fl = floor;
      await act(
        { op: 'remove_furniture', furnitureId: p.id },
        {
          undo: () =>
            callDialogue({
              op: 'place_furniture',
              floor: fl,
              furnitureId: p.id,
              x: p.x,
              y: p.y,
              rotation: p.rotation,
            }),
          redo: () => callDialogue({ op: 'remove_furniture', furnitureId: p.id }),
        },
      );
    } else if (mode.kind === 'staircase' && owned) {
      await act({ op: 'build_staircase', floor, x, y });
    }
  }

  function button(label: string, onClick: () => void, active = false): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = active ? 'wca-choice wca-apt-active' : 'wca-choice';
    b.textContent = label;
    b.addEventListener('click', onClick);
    return b;
  }

  function render(): void {
    panel.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'wca-inventory-head';
    head.textContent = view
      ? `Apartment — ⌂ ${view.totalTiles} tiles · ✨ Aura ${view.aura} · ⛃ ${view.metaMoney} meta`
      : 'Apartment';
    panel.appendChild(head);
    if (!view) return;

    // Floor selector.
    const floors = document.createElement('div');
    floors.className = 'wca-apt-toolbar';
    for (const f of view.apartment.floors) {
      floors.appendChild(
        button(
          `Floor ${f.index}`,
          () => {
            floor = f.index;
            render();
          },
          floor === f.index,
        ),
      );
    }
    panel.appendChild(floors);

    // Grid.
    const f = view.apartment.floors.find((x) => x.index === floor) ?? view.apartment.floors[0]!;
    const grid = document.createElement('div');
    grid.className = 'wca-apt-grid';
    for (let y = 0; y < 10; y++) for (let x = 0; x < 10; x++) grid.appendChild(cellButton(x, y, f));
    panel.appendChild(grid);

    // Modes.
    const tools = document.createElement('div');
    tools.className = 'wca-apt-toolbar';
    tools.append(
      button(
        `Buy tile (${view.nextTileCost})`,
        () => {
          mode = { kind: 'buy', tileKind: 'indoor' };
          render();
        },
        mode.kind === 'buy' && mode.tileKind === 'indoor',
      ),
      button(
        `Buy garden (${view.nextTileCost})`,
        () => {
          mode = { kind: 'buy', tileKind: 'garden' };
          render();
        },
        mode.kind === 'buy' && mode.tileKind === 'garden',
      ),
      button(
        'Remove',
        () => {
          mode = { kind: 'remove' };
          render();
        },
        mode.kind === 'remove',
      ),
      button(
        f.tiles.length >= view.staircaseUnlockAt
          ? `Staircase (${view.staircaseCost})`
          : `Staircase 🔒 ${f.tiles.length}/${view.staircaseUnlockAt}`,
        () => {
          mode = { kind: 'staircase' };
          render();
        },
        mode.kind === 'staircase',
      ),
    );
    panel.appendChild(tools);

    // Stored furniture to place.
    if (view.apartment.stored.length > 0) {
      const store = document.createElement('div');
      store.className = 'wca-apt-toolbar';
      for (const s of view.apartment.stored) {
        const def = view.catalog.find((c) => c.type === s.type);
        store.appendChild(
          button(
            `Place ${def?.name ?? s.type}`,
            () => {
              mode = { kind: 'place', furnitureId: s.id, rotation: 0 };
              render();
            },
            mode.kind === 'place' && mode.furnitureId === s.id,
          ),
        );
      }
      if (mode.kind === 'place') {
        const m = mode;
        store.appendChild(
          button(`Rotate (${m.rotation}°)`, () => {
            mode = { ...m, rotation: ((m.rotation + 90) % 360) as 0 | 90 | 180 | 270 };
            render();
          }),
        );
      }
      panel.appendChild(store);
    }

    // Cross-week storage (US 02.04) — the fridge/closet surface until the
    // walkable apartment exists.
    if (view.storage) {
      const store = document.createElement('div');
      store.className = 'wca-apt-toolbar';
      const label = document.createElement('span');
      label.textContent = `Storage ${view.storage.length}/${view.storageCapacity ?? '?'} stacks:`;
      store.appendChild(label);
      if (view.storage.length === 0) {
        const empty = document.createElement('span');
        empty.textContent = ' empty';
        store.appendChild(empty);
      }
      for (const s of view.storage) {
        store.appendChild(
          button(`⬆ ${s.itemId.replace('itm_', '')} ×${s.qty}`, () => {
            void callDialogue<View>({ op: 'withdraw_item', itemId: s.itemId, qty: s.qty }).then(
              (r) => {
                if (r.ok) void refresh();
                else {
                  notice = r.message;
                  render();
                }
              },
            );
          }),
        );
      }
      panel.appendChild(store);
    }

    // Undo / redo (furniture ops).
    const history = document.createElement('div');
    history.className = 'wca-apt-toolbar';
    const undoBtn = button('↶ Undo', () => {
      const e = undoStack.pop();
      if (e) {
        redoStack.push(e);
        void e.undo().then(refresh);
      }
    });
    undoBtn.disabled = undoStack.length === 0;
    const redoBtn = button('↷ Redo', () => {
      const e = redoStack.pop();
      if (e) {
        undoStack.push(e);
        void e.redo().then(refresh);
      }
    });
    redoBtn.disabled = redoStack.length === 0;
    history.append(undoBtn, redoBtn);
    panel.appendChild(history);

    if (notice) {
      const p = document.createElement('p');
      p.className = 'wca-apt-notice';
      p.textContent = notice;
      panel.appendChild(p);
    }
  }

  worldBus.on('ui-intent', ({ intent }) => {
    if (intent === 'OPEN_APARTMENT') {
      open = !open;
      panel.classList.toggle('hidden', !open);
      if (open) {
        render();
        void refresh();
      }
    }
    if (intent === 'CANCEL' && open) {
      open = false;
      panel.classList.add('hidden');
    }
  });
}
